import { useState, useEffect } from 'react';
import { mockRisks } from '@/data/mockRisks';
import { Risk } from '@/types';

const RISKS_STORAGE_KEY = 'mock_risks';

export const useMockRisks = (projectId?: string) => {
  const [risks, setRisks] = useState<Risk[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!projectId) {
      setIsLoading(false);
      return;
    }

    const timer = setTimeout(() => {
      try {
        const stored = localStorage.getItem(RISKS_STORAGE_KEY);
        let allRisks = stored ? JSON.parse(stored) : mockRisks;
        
        // Convert date strings back to Date objects
        allRisks = allRisks.map((r: any) => ({
          ...r,
          dateIdentified: new Date(r.dateIdentified),
          dateUpdated: new Date(r.dateUpdated)
        }));

        const projectRisks = allRisks.filter((r: Risk) => r.projectId === projectId);
        setRisks(projectRisks);
        setIsLoading(false);
      } catch (err) {
        setError(err as Error);
        setIsLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [projectId]);

  return { data: risks, isLoading, error, enabled: !!projectId };
};

export const useMockCreateRisk = () => {
  const [isLoading, setIsLoading] = useState(false);

  const mutateAsync = async (riskData: Partial<Risk> & { projectId: string }) => {
    setIsLoading(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const newRisk: Risk = {
        id: `risk-${Date.now()}`,
        name: riskData.name!,
        description: riskData.description || '',
        projectId: riskData.projectId,
        probability: riskData.probability!,
        impact: riskData.impact!,
        owner: riskData.owner!,
        mitigation: riskData.mitigation || '',
        status: riskData.status || 'open',
        stage: riskData.stage!,
        dateIdentified: new Date(),
        dateUpdated: new Date()
      };

      const stored = localStorage.getItem(RISKS_STORAGE_KEY);
      const currentRisks = stored ? JSON.parse(stored) : mockRisks;
      const updatedRisks = [...currentRisks, newRisk];
      localStorage.setItem(RISKS_STORAGE_KEY, JSON.stringify(updatedRisks));
      
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      throw err;
    }
  };

  return { mutate: mutateAsync, mutateAsync, isLoading };
};

export const useMockUpdateRisk = () => {
  const [isLoading, setIsLoading] = useState(false);

  const mutateAsync = async ({ id, projectId, ...updates }: Partial<Risk> & { id: string; projectId: string }) => {
    setIsLoading(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const stored = localStorage.getItem(RISKS_STORAGE_KEY);
      const currentRisks = stored ? JSON.parse(stored) : mockRisks;
      
      const updatedRisks = currentRisks.map((risk: Risk) => 
        risk.id === id 
          ? { ...risk, ...updates, dateUpdated: new Date() }
          : risk
      );
      
      localStorage.setItem(RISKS_STORAGE_KEY, JSON.stringify(updatedRisks));
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      throw err;
    }
  };

  return { mutate: mutateAsync, mutateAsync, isLoading };
};